/* global Excel console */

import { AccountManager } from "./authConfig";

export async function insertText(text: string) {
  // Write text to the top left cell.
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange("A1");
      range.values = [[text]];
      range.format.autofitColumns();
      await context.sync();
    });
  } catch (error) {
  }
}

export async function getUserSelectedRange(): Promise<Excel.Range | null> {
  try {
    return await Excel.run(async (context) => {
      let range = context.workbook.getSelectedRange();
      range.load(["addressLocal", "values"]);
      await context.sync();
      return range;
    });
  } catch (error) {
    return null;
  }
}

export async function insertTextToActiveCell(text: string | number) {
  try {
    return await Excel.run(async (context) => {
      let range = context.workbook.getSelectedRange();
      range.load(["addressLocal", "values"]);
      await context.sync();
      range.values = [[text]];
      await context.sync();
    });
  } catch (error) {
    return null;
  }
}

export async function getAddressLocalFromUserSelectedRange(): Promise<string | null> {
  try {
    return await Excel.run(async (context) => {
      let range = context.workbook.getSelectedRange();
      range.load("addressLocal");
      await context.sync();
      return range.addressLocal;
    });
  } catch (error) {
    return null;
  }
}

export async function getValuesFromRange(range: Excel.Range): Promise<any[] | null> {
  try {
    return await Excel.run(async (context) => {
      range.load("values");
      await context.sync();
      return range.values;
    });
  } catch (error) {
    return null;
  }
}

export async function getValuesFromUserSelectedRange(): Promise<any[] | null> {
  try {
    return await Excel.run(async (context) => {
      let range = context.workbook.getSelectedRange();
      range.load("values");
      await context.sync();
      return range.values;
    });
  } catch (error) {
    return null;
  }
}

export async function getAddressLocalFromRange(range: Excel.Range): Promise<string | null> {
  try {
    return await Excel.run(async (context) => {
      range.load("addressLocal");
      await context.sync();
      return range.addressLocal;
    });
  } catch (error) {
    return null;
  }
}

export async function getAddressLocalFromUserTypedAddress(address): Promise<string | null> {
  try {
    return await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange(address);
      range.load("addressLocal");
      await context.sync();
      return range.addressLocal;
    });
  } catch (error) {
    return null;
  }
}

export async function getValuesFromUserTypedAddress(address: string): Promise<number[][] | null> {
  try {
    return await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange(address);
      range.load("values");
      await context.sync();
      const values = range.values;
      return values;
    });
  } catch (error) {
    return null;
  }
}

export async function clearCellColors(cellAddresses: string[]) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      for (const cellAddress of cellAddresses) {
        const range = sheet.getRange(cellAddress);
        const rangeFill = range.format.fill;
        rangeFill.clear();
      }
      await context.sync();
    });
  } catch (error) {
    return null;
  }
}

export async function highlightCells(cellAddresses: string[], color: string) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      for (const cellAddress of cellAddresses) {
        const range = sheet.getRange(cellAddress);
        const rangeFill = range.format.fill;
        if (color === "none") {
          rangeFill.clear();
        } else {
          rangeFill.color = color;
        }
      }
      await context.sync();
    });
  } catch (error) {
    return null;
  }
}

export async function getCellColors(cellAddresses: string[]) {
  try {
    let colors: string[] = [];
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      for (const cellAddress of cellAddresses) {
        const range = sheet.getRange(cellAddress);
        const rangeFill = range.format.fill;
        rangeFill.load("color"); // Explicitly load
        await context.sync();  // Ensures color is loaded
        const color = rangeFill.color;
        colors.push(color);
      }
    });
    return colors;
  } catch (error) {
    return null;
  }
}

export async function getUserData(accountManager: AccountManager) {
  try {
    const userAccount = await accountManager.ssoGetUserIdentity(["user.read"]);

    return userAccount;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function run(address: string) {
  try {
    await Excel.run(async (context) => {
      /**
       * Insert your Excel code here
       */
      const range = context.workbook.worksheets.getActiveWorksheet().getRange(address);
      // Read the range address
      range.load("address");

      // Update the fill color
      range.format.fill.color = "yellow";

      await context.sync();
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 * Expands an Excel range address (e.g. "A1:C2") into
 * an array of individual cell addresses.
 *
 * Example:
 *  expandRange("A1:A2") => ["A1", "A2"]
 *  expandRange("A1:C2") => ["A1","B1","C1","A2","B2","C2"]
 */
export function expandRange(rangeAddress: string): string[] {
    const [start, end] = rangeAddress.toUpperCase().split(":");

    if (!end) {
        return [start]; // Single cell like "A1"
    }

    const startMatch = start.match(/^([A-Z]+)(\d+)$/);
    const endMatch = end.match(/^([A-Z]+)(\d+)$/);

    if (!startMatch || !endMatch) {
        throw new Error("Invalid range format");
    }

    const startCol = startMatch[1];
    const startRow = parseInt(startMatch[2], 10);
    const endCol = endMatch[1];
    const endRow = parseInt(endMatch[2], 10);

    const result: string[] = [];

    const startColIndex = columnToNumber(startCol);
    const endColIndex = columnToNumber(endCol);

    for (let col = startColIndex; col <= endColIndex; col++) {
        for (let row = startRow; row <= endRow; row++) {
            result.push(`${numberToColumn(col)}${row}`);
        }
    }

    return result;
}

/** Converts column letters (e.g. "A", "AA") to number */
function columnToNumber(column: string): number {
    let number = 0;
    for (let i = 0; i < column.length; i++) {
        number = number * 26 + (column.charCodeAt(i) - 64);
    }
    return number;
}

/** Converts number back to column letters */
function numberToColumn(num: number): string {
    let column = "";
    while (num > 0) {
        const remainder = (num - 1) % 26;
        column = String.fromCharCode(65 + remainder) + column;
        num = Math.floor((num - 1) / 26);
    }
    return column;
}

export type FormulaNameStr = "SUM" | "AVERAGE" | "MAX" | "MIN"; // Extend as needed

export async function formulaByColor(cellRangeAddress: string, cellColorAddress: string, formula: FormulaNameStr
): Promise<number | string> {
    const result = await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        // Range to check
        const range = sheet.getRange(cellRangeAddress);
        range.load(["values", "format/fill/color"]);

        // Reference cell to match color
        const referenceCell = sheet.getRange(cellColorAddress);
        referenceCell.load("format/fill/color");

        await context.sync();
        const referenceColor = referenceCell.format.fill.color;
        
        const cells = expandRange(cellRangeAddress);
        
        const colorCells: Excel.Range[] = [];
        for (const cellAddress of cells) {
          const cell = sheet.getRange(cellAddress);
          await context.sync(); // Ensure cell is loaded before accessing properties
          cell.load(["values", "format/fill/color"]);
          await context.sync(); // Ensure cell is loaded before accessing properties
          const cellColor = cell.format.fill.color;
          await context.sync();
          if (cellColor === referenceColor) {
            colorCells.push(cell);
          }
        };
        if (formula === "SUM") {
        const result = context.workbook.functions.sum(
          ...colorCells
        );
        result.load("value");
        await context.sync();
        return result.value;
      } else if (formula === "AVERAGE") {
        const result = context.workbook.functions.average(
          ...colorCells
        );
        result.load("value");
        await context.sync();

        return result.value;
      } else if (formula === "MAX") {
        const result = context.workbook.functions.max(
          ...colorCells
        );
        result.load("value");
        await context.sync();

        return result.value;
      } else if (formula === "MIN") {
        const result = context.workbook.functions.min(
          ...colorCells
        );
        result.load("value");
        await context.sync();

        return result.value;
      } else {
        return "Unsupported formula: " + formula;
      }
    });
    return result;
}